import dagre from '@dagrejs/dagre'
import { Add, Close, SyncProblem } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  Stack,
  SvgIcon,
  Tooltip,
  TooltipProps,
  Typography
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { withObservables } from '@nozbe/watermelondb/react'
import {
  Background,
  BaseEdge,
  Connection,
  ConnectionLineComponentProps,
  ConnectionMode,
  Controls,
  Edge,
  EdgeLabelRenderer,
  EdgeProps,
  getBezierPath,
  Handle,
  IsValidConnection,
  MarkerType,
  MiniMap,
  Node,
  NodeProps,
  OnConnectStartParams,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useOnViewportChange,
  useReactFlow
} from '@xyflow/react'
import { calendarsGif } from 'assets/gif'
import { CloseIcon } from 'assets/icons'
import { NodeError } from 'assets/icons/group-details'
import * as CardsStyled from 'components/cards/styled'
import { usePayment } from 'components/providers/PaymentProvider'
import { tierFeaturesToAnalyticsFeature } from 'components/tier/OutOfTierDialog'
import { tierToPlan } from 'components/tier/TierHover'
import { AnimatePresence, Reorder } from 'framer-motion'
import i18n, { t } from 'i18next'
import {
  ComponentType,
  DragEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { inheritanceStatus, inheritFromGroups } from 'services/api/group'
import ObserveService from 'services/db/Observe'
import { AppConstantsQuery } from 'services/db/Queries/AppConstantQueries'
import { allGroupsForInheritance } from 'services/db/Queries/GroupQueries'
import { UserSettings } from 'services/db/Queries/UserSettingsQueries'
import LocalStorage from 'services/LocalStorage'
import {
  AccountDoc,
  GroupDoc,
  ParentGroupLinkMode,
  ParentGroupLinkValue,
  UserSettingsDoc
} from 'types/api/changes'
import { InheritanceIssue, InheritFromGroupsError } from 'types/api/group'
import { LocalStorageKey } from 'types/services/localStorage'
import { highestActiveTier, Tier } from 'utils/api/accounts'
import { TierFeatures } from 'utils/api/group_state'
import {
  defaultGroupImage,
  isCalendarLinkingAllowed,
  isUserActiveInGroup,
  isUserAdminInGroup
} from 'utils/api/groups'
import { isGroupArchived, isGroupDeleted } from 'utils/api/userSettings'

import {
  addParentConnection,
  bidirectionalEdgePath,
  CalendarGraph,
  graphEdgeHandles,
  graphEdges,
  graphFromGroups,
  inheritancePayload,
  isSelfConnection,
  mergeGraphIssues,
  removeCalendarNode,
  removeParentConnection,
  setParentConnectionMode
} from './graph'

const NODE_WIDTH = 248
const NODE_HEIGHT = 84
const CONTENT_ZOOM_THRESHOLD = 0.75
const PREVIEW_ZOOM = 0.98
const DEMO_FIT_DELAY = 350
const PREVIEW_RESIZE_SETTLE_DELAY = 450
const MAX_STATUS_SUBJECTS_PER_REQUEST = 100

interface CalendarNodeData extends Record<string, unknown> {
  groupId: string
  name: string
  color: string
  photo?: string
  fallbackPhoto?: string
  showDetails: boolean
  isShaking: boolean
  warning?: string
  onRemove: (groupId: string) => void
}

interface GraphIssueEdge {
  parentId: string
  childId: string
  message: string
}

interface DemoCanvasProps {
  visibleGroupIds: Set<string>
  onVisibleGroupIdsChange: (groupIds: Set<string>) => void
  removedEdgeIds: Set<string>
  onRemovedEdgeIdsChange: (edgeIds: Set<string>) => void
}

interface DemoCalendarDefinition {
  groupId: string
  labelKey: string
  color: string
  photo: string
}

const demoCalendarDefinitions: DemoCalendarDefinition[] = [
  {
    groupId: 'demo-source-a',
    labelKey: 'linkedCalendarsExampleSourceCalendarA',
    color: '#7f8aa5',
    photo:
      'https://s3.amazonaws.com/res.twentyfour.me/GroupCal/defaultGroupsPhotos/workGroupPhoto2.png'
  },
  {
    groupId: 'demo-source-b',
    labelKey: 'linkedCalendarsExampleSourceCalendarB',
    color: '#6d8cff',
    photo:
      'https://s3.amazonaws.com/res.twentyfour.me/GroupCal/defaultGroupsPhotos/familyGroupPhoto1.png'
  },
  {
    groupId: 'demo-destination',
    labelKey: 'linkedCalendarsExampleDestinationCalendar',
    color: '#5f7bb8',
    photo:
      'https://s3.amazonaws.com/res.twentyfour.me/GroupCal/defaultGroupsPhotos/vacationGroupPhoto1.png'
  }
]

const demoCalendarEdgeDefinitions = [
  {
    id: 'demo-source-a->demo-destination',
    source: 'demo-source-a',
    target: 'demo-destination',
    mode: ParentGroupLinkMode.Propagating
  },
  {
    id: 'demo-source-b->demo-destination',
    source: 'demo-source-b',
    target: 'demo-destination',
    mode: ParentGroupLinkMode.Terminal
  }
] as const

function inheritanceIssueMessage(issue: { code?: number; reason?: string }) {
  switch (issue.reason) {
    case 'SelfInheritanceNotAllowed':
      return t('linkedCalendarSelfLink')
    case 'InheritanceDepthLimitExceeded':
      return t('inheritanceDepthLimitNotice')
    case 'CalendarLinkingNotAllowed':
      return t('linkCalendarParentBlockedAddingMembers')
    case 'RequesterNotActiveParticipant':
      return t('linkCalendarParentBlockedActiveParticipant')
    case 'RequesterNotActiveAdmin':
      return t('linkCalendarChildBlockedNonAdmin')
    case 'CalendarOwnerTierRequired':
      return t('linkedCalendarOwnerTierRequired')
    default:
      break
  }

  switch (issue.code) {
    case -103:
      return t('linkCalendarChildBlockedNonAdmin')
    case -104:
      return t('linkCalendarParentBlockedActiveParticipant')
    case -109:
    case -119:
      return t('linkCalendarParentBlockedAddingMembers')
    case -118:
      return t('linkedCalendarOwnerTierRequired')
    case -123:
      return t('linkedCalendarGroupOwnerTierRequired')
    default:
      return issue.reason ?? t('linkedCalendarsSaveFailed')
  }
}

function chunkArray<T>(items: T[], chunkSize: number) {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize))
  }
  return chunks
}

interface GraphEdgeData extends Record<string, unknown> {
  canRemove: boolean
  reverseEdgeExists?: boolean
  mode?: ParentGroupLinkValue
  errorMessage?: string
  tooltipPlacement?: TooltipProps['placement']
  onUpgrade?: () => void
  onRemove: (edgeId: string) => void
  onModeChange?: (edgeId: string, mode: ParentGroupLinkValue) => void
}

export interface LinkedCalendarsEditorProps {
  initialGroupId?: string
  onDirtyChange?: (dirty: boolean) => void
  withCloseButton?: boolean
  closeAction?: () => void
}

interface LinkedCalendarsEditorContentProps extends LinkedCalendarsEditorProps {
  account?: AccountDoc
}

function eventClientPoint(event: MouseEvent | TouchEvent) {
  if ('changedTouches' in event && event.changedTouches.length > 0) {
    return {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY
    }
  }

  return {
    x: (event as MouseEvent).clientX,
    y: (event as MouseEvent).clientY
  }
}

function nodeIdAtEventPoint(event: MouseEvent | TouchEvent) {
  const point = eventClientPoint(event)
  const element = document.elementFromPoint(point.x, point.y)
  return element?.closest('.react-flow__node')?.getAttribute('data-id')
}

function nodeBoundsCenter(targetNodes: Node<CalendarNodeData>[]) {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const node of targetNodes) {
    minX = Math.min(minX, node.position.x)
    minY = Math.min(minY, node.position.y)
    maxX = Math.max(maxX, node.position.x + NODE_WIDTH)
    maxY = Math.max(maxY, node.position.y + NODE_HEIGHT)
  }

  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2
  }
}

function horizontalTooltipPlacement(sourceX: number, targetX: number) {
  const targetIsToRight = targetX >= sourceX
  const isRtl = i18n.dir() === 'rtl'

  return targetIsToRight === isRtl ? 'left' : 'right'
}

function layoutNodes(nodes: Node<CalendarNodeData>[], edges: Edge[]) {
  const layout = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}))
  layout.setGraph({ rankdir: 'TB', ranksep: 100, nodesep: 56 })

  nodes.forEach((node) => {
    layout.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  })

  edges.forEach((edge) => {
    layout.setEdge(edge.source, edge.target)
  })

  dagre.layout(layout)

  return nodes.map((node) => {
    const position = layout.node(node.id)
    return {
      ...node,
      position: {
        x: position.x - NODE_WIDTH / 2,
        y: position.y - NODE_HEIGHT / 2
      }
    }
  })
}

function demoGraph(visibleGroupIds: Set<string>, removedEdgeIds: Set<string>) {
  const nodes = demoCalendarDefinitions
    .filter((definition) => visibleGroupIds.has(definition.groupId))
    .map((definition) => ({
      id: definition.groupId,
      type: 'calendar',
      position: { x: 0, y: 0 },
      data: {
        groupId: definition.groupId,
        name: t(definition.labelKey),
        color: definition.color,
        photo: definition.photo,
        showDetails: true,
        isShaking: false,
        onRemove: () => {}
      }
    })) as Node<CalendarNodeData>[]

  const edges: Edge<GraphEdgeData>[] = demoCalendarEdgeDefinitions
    .filter((definition) => !removedEdgeIds.has(definition.id))
    .map((definition) => ({
      id: definition.id,
      source: definition.source,
      target: definition.target,
      type: 'removableBezier',
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#76819a'
      },
      style: {
        stroke: '#76819a',
        strokeDasharray: '4 6',
        strokeWidth: 2
      },
      data: {
        canRemove: true,
        mode: definition.mode,
        onRemove: () => {}
      }
    }))

  const visibleEdges = edges.filter(
    (edge) =>
      visibleGroupIds.has(edge.source) && visibleGroupIds.has(edge.target)
  )

  return { nodes: layoutNodes(nodes, visibleEdges), edges: visibleEdges }
}

function LinkedCalendarsEmptyCanvas(props: DemoCanvasProps) {
  const demo = useMemo(
    () => demoGraph(props.visibleGroupIds, props.removedEdgeIds),
    [props.removedEdgeIds, props.visibleGroupIds]
  )
  const [nodes, setNodes, onNodesChange] = useNodesState<
    Node<CalendarNodeData>
  >(demo.nodes)
  const [edges, setEdges] = useState<Edge<GraphEdgeData>[]>(demo.edges)
  const { setCenter } = useReactFlow()
  const demoFitTimeoutRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    return () => {
      window.clearTimeout(demoFitTimeoutRef.current)
    }
  }, [])

  const nodeCenters = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>()
    nodes.forEach((node) => {
      map.set(node.id, {
        x: node.position.x + NODE_WIDTH / 2,
        y: node.position.y + NODE_HEIGHT / 2
      })
    })
    return map
  }, [nodes])

  const removeDemoNode = useCallback(
    (groupId: string) => {
      const nextVisibleIds = new Set(props.visibleGroupIds)
      nextVisibleIds.delete(groupId)
      props.onVisibleGroupIdsChange(nextVisibleIds)

      const nextRemovedEdgeIds = new Set(props.removedEdgeIds)
      demoCalendarEdgeDefinitions.forEach((edge) => {
        if (edge.source === groupId || edge.target === groupId) {
          nextRemovedEdgeIds.add(edge.id)
        }
      })
      props.onRemovedEdgeIdsChange(nextRemovedEdgeIds)

      const nextNodes = nodes.filter((node) => node.id !== groupId)

      if (nextNodes.length === 0) {
        setNodes([])
        setEdges([])
        return
      }

      setNodes(nextNodes)
      setEdges((currentEdges) =>
        currentEdges.filter(
          (edge) => edge.source !== groupId && edge.target !== groupId
        )
      )
    },
    [nodes, props, setNodes]
  )

  const removeDemoEdge = useCallback((edgeId: string) => {
    setEdges((currentEdges) =>
      currentEdges.filter((edge) => edge.id !== edgeId)
    )
  }, [])

  const changeDemoEdgeMode = useCallback(
    (edgeId: string, mode: ParentGroupLinkValue) => {
      setEdges((currentEdges) =>
        currentEdges.map((edge) =>
          edge.id === edgeId && edge.data
            ? { ...edge, data: { ...edge.data, mode } }
            : edge
        )
      )
    },
    []
  )

  const renderedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          onRemove: removeDemoNode
        }
      })),
    [nodes, removeDemoNode]
  )

  const renderedEdges = useMemo(
    () =>
      edges.map((edge) => {
        const sourceCenter = nodeCenters.get(edge.source)
        const targetCenter = nodeCenters.get(edge.target)
        const handles =
          sourceCenter && targetCenter
            ? graphEdgeHandles(
                edge.source,
                edge.target,
                sourceCenter,
                targetCenter,
                edges.some(
                  (candidate) =>
                    candidate.source === edge.target &&
                    candidate.target === edge.source
                )
              )
            : { source: 'bottom', target: 'top' }
        const tooltipPlacement: TooltipProps['placement'] =
          sourceCenter && targetCenter
            ? horizontalTooltipPlacement(sourceCenter.x, targetCenter.x)
            : 'right'

        return {
          ...edge,
          type: 'removableBezier',
          sourceHandle: handles.source,
          targetHandle: handles.target,
          data: {
            ...(edge.data ?? {}),
            canRemove: true,
            reverseEdgeExists: edges.some(
              (candidate) =>
                candidate.source === edge.target &&
                candidate.target === edge.source
            ),
            tooltipPlacement,
            onRemove: removeDemoEdge,
            onModeChange: changeDemoEdgeMode
          }
        }
      }),
    [changeDemoEdgeMode, edges, nodeCenters, removeDemoEdge]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return

      if (isSelfConnection(connection.target, connection.source)) return

      setEdges((currentEdges) => [
        ...currentEdges,
        {
          id: `${connection.source}->${connection.target}`,
          source: connection.source,
          target: connection.target,
          type: 'removableBezier',
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#76819a'
          },
          style: {
            stroke: '#76819a',
            strokeDasharray: '4 6',
            strokeWidth: 2
          },
          data: {
            canRemove: true,
            mode: ParentGroupLinkMode.Propagating,
            onRemove: removeDemoEdge
          }
        }
      ])
    },
    [removeDemoEdge]
  )

  return (
    <ReactFlow
      nodes={renderedNodes}
      edges={renderedEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onInit={() => {
        demoFitTimeoutRef.current = window.setTimeout(() => {
          if (nodes.length === 0) return

          const center = nodeBoundsCenter(nodes)
          setCenter(center.x, center.y, {
            zoom: PREVIEW_ZOOM,
            duration: 200
          })
        }, DEMO_FIT_DELAY)
      }}
      onConnect={onConnect}
      onNodesDelete={(deleted) => {
        deleted.forEach((node) => removeDemoNode(node.id))
      }}
      nodesDraggable
      nodesConnectable
      connectionMode={ConnectionMode.Loose}
      panOnScroll
      minZoom={0.45}
      maxZoom={1.6}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={20} size={1} color="#d7deeb" />
      <Controls showInteractive={false} />
      <MiniMap
        className="linked-calendars-minimap"
        position="bottom-right"
        pannable
        zoomable
        nodeStrokeWidth={2}
        maskColor="rgba(44, 57, 85, 0.14)"
        maskStrokeColor="rgba(44, 57, 85, 0.46)"
        maskStrokeWidth={2}
        nodeColor={(node) =>
          ((node.data as CalendarNodeData | undefined)?.color as
            | string
            | undefined) ?? '#95a2bf'
        }
      />
    </ReactFlow>
  )
}

function connectionTooltipTitle(errorMessage: string, onUpgrade?: () => void) {
  return (
    <Stack alignItems="center" spacing={1} sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 14, textAlign: 'center' }}>
        {errorMessage}
      </Typography>
      {onUpgrade && (
        <Button
          className="nopan nodrag"
          size="small"
          variant="contained"
          color="error"
          onClick={(event) => {
            event.stopPropagation()
            onUpgrade()
          }}
          sx={{
            borderRadius: 999,
            textTransform: 'none',
            paddingInline: 2
          }}
        >
          {t('upgrade')}
        </Button>
      )}
    </Stack>
  )
}

function CalendarFlowIcon({ mode }: { mode: ParentGroupLinkValue }) {
  const terminal = mode === ParentGroupLinkMode.Terminal

  return (
    <SvgIcon sx={{ width: 40, height: 40 }} viewBox="0 0 38 24">
      <rect
        x="8"
        y="5.75"
        width="20"
        height="12.5"
        rx="2"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.95"
      />
      <path
        d={
          terminal
            ? 'M1.5 12H16m-3-3 3 3-3 3'
            : 'M1.5 12h12.9m7.2 0H35m-3-3 3 3-3 3'
        }
        fill="none"
        stroke="currentColor"
        strokeWidth="0.95"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={terminal ? 20.9 : 18} cy="12" r="3.6" />
    </SvgIcon>
  )
}

function RemovableBezierEdge(props: EdgeProps<Edge<GraphEdgeData>>) {
  const [edgePath, labelX, labelY] = props.data?.reverseEdgeExists
    ? bidirectionalEdgePath(
        { x: props.sourceX, y: props.sourceY },
        { x: props.targetX, y: props.targetY },
        props.sourcePosition,
        props.targetPosition
      )
    : getBezierPath(props)
  const errorMessage = props.data?.errorMessage
  const onUpgrade = props.data?.onUpgrade
  const tooltipPlacement = props.data?.tooltipPlacement ?? 'right'
  const mode = props.data?.mode
  const nextMode =
    mode === ParentGroupLinkMode.Terminal
      ? ParentGroupLinkMode.Propagating
      : ParentGroupLinkMode.Terminal
  const modeControl = mode ? (
    <Tooltip
      title={t(
        mode === ParentGroupLinkMode.Terminal
          ? 'linkedCalendarTerminalMode'
          : 'linkedCalendarPropagatingMode'
      )}
      slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
    >
      <span>
        <IconButton
          className="nopan nodrag linked-calendar-edge-mode-button"
          size="small"
          disabled={!props.data?.canRemove}
          aria-label={t(
            nextMode === ParentGroupLinkMode.Terminal
              ? 'linkedCalendarSwitchToTerminal'
              : 'linkedCalendarSwitchToPropagating'
          )}
          onClick={() => props.data?.onModeChange?.(props.id, nextMode)}
        >
          <CalendarFlowIcon mode={mode} />
        </IconButton>
      </span>
    </Tooltip>
  ) : null

  const tooltipTitle = errorMessage
    ? connectionTooltipTitle(errorMessage, onUpgrade)
    : null

  return (
    <>
      <BaseEdge
        id={props.id}
        path={edgePath}
        markerEnd={props.markerEnd}
        style={props.style}
      />
      <EdgeLabelRenderer>
        {errorMessage ? (
          <Tooltip
            open
            arrow
            placement={tooltipPlacement}
            title={tooltipTitle}
            slotProps={{
              popper: {
                // Render the popper inside the React Flow viewport (the same
                // transformed layer that hosts the anchor) so canvas pan/zoom
                // moves the tooltip together with the button instead of
                // letting them detach.
                disablePortal: true
              },
              tooltip: {
                className: 'linked-calendar-error-tooltip',
                sx: {
                  direction: 'ltr',
                  fontSize: 14,
                  maxWidth: 260,
                  padding: '8px'
                }
              }
            }}
          >
            <div
              className="linked-calendar-edge-action is-error"
              style={{
                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`
              }}
            >
              {modeControl}
              <IconButton
                className="nopan nodrag"
                size="small"
                aria-label={t('unlinkCalendars')}
                onClick={() => props.data?.onRemove(props.id)}
              >
                <CloseIcon width={25} height={25} color="currentColor" />
              </IconButton>
            </div>
          </Tooltip>
        ) : (
          <div
            className="linked-calendar-edge-action"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`
            }}
          >
            {modeControl}
            <IconButton
              className="nopan nodrag"
              size="small"
              aria-label={t('unlinkCalendars')}
              onClick={() => props.data?.onRemove(props.id)}
            >
              <CloseIcon width={25} height={25} color="currentColor" />
            </IconButton>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  )
}

interface CalendarChipVisualProps {
  name: string
  color: string
  photo?: string
  fallbackPhoto?: string
  warning?: string
  showProblem?: boolean
  problemTitle?: string
  rightActions?: React.ReactNode
}

function CalendarChipVisual(props: CalendarChipVisualProps) {
  return (
    <Box className="linked-calendar-chip">
      <CardsStyled.GroupCardAvatarContainer
        color={props.color}
        width={40}
        height={40}
      >
        <CardsStyled.GroupCardAvatar
          asGroup
          size={34}
          img={props.photo}
          name={props.name}
          emptyPic={props.fallbackPhoto}
        />
      </CardsStyled.GroupCardAvatarContainer>
      <Box className="linked-calendar-chip-body" minWidth={0}>
        <Box className="linked-calendar-title-row">
          <Typography className="linked-calendar-title">
            {props.name}
          </Typography>
          {props.warning !== undefined && (
            <Tooltip title={props.warning}>
              <Box className="linked-calendar-warning" component="span">
                <NodeError width={14} height={14} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
      {(props.showProblem || props.rightActions) && (
        <Box className="linked-calendar-actions">
          {props.showProblem && (
            <Tooltip title={props.problemTitle ?? ''}>
              <SyncProblem
                className="linked-calendar-problem"
                fontSize="small"
              />
            </Tooltip>
          )}
          {props.rightActions}
        </Box>
      )}
    </Box>
  )
}

interface CalendarCardShellProps {
  color: string
  className?: string
  compact?: boolean
  isSelected?: boolean
  isShaking?: boolean
  showHandles?: boolean
  children: React.ReactNode
  noShadow?: boolean
}

function CalendarCardShell(props: CalendarCardShellProps) {
  const rootClasses = [
    'linked-calendar-card',
    props.isShaking ? 'is-shaking' : '',
    props.compact ? 'is-compact' : '',
    props.isSelected ? 'is-selected' : ''
  ]
    .filter(Boolean)
    .join(' ')

  const handlePositions: {
    id: string
    position: Position
    className: string
  }[] = [
    { id: 'top', position: Position.Top, className: 'top' },
    { id: 'right', position: Position.Right, className: 'right' },
    { id: 'bottom', position: Position.Bottom, className: 'bottom' },
    { id: 'left', position: Position.Left, className: 'left' }
  ]

  return (
    <Box
      className={rootClasses}
      sx={{
        borderColor: props.color,
        boxShadow: props.noShadow ? 'none' : undefined
      }}
    >
      {props.showHandles &&
        handlePositions.map((entry) => (
          <Tooltip key={entry.id} title="" disableHoverListener>
            <Handle
              id={entry.id}
              className={`linked-calendar-handle ${entry.className}`}
              type="source"
              position={entry.position}
            />
          </Tooltip>
        ))}
      {props.children}
    </Box>
  )
}

function CalendarNode({ data, selected }: NodeProps<Node<CalendarNodeData>>) {
  const showContent = data.showDetails

  const onRemove = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    data.onRemove(data.groupId)
  }

  return (
    <CalendarCardShell
      color={data.color}
      compact={!showContent}
      isSelected={selected}
      isShaking={data.isShaking}
      showHandles
    >
      {/* Only show in case of selected */}
      {showContent && (
        <CalendarChipVisual
          name={data.name}
          color={data.color}
          photo={data.photo}
          fallbackPhoto={data.fallbackPhoto}
          warning={data.warning}
          rightActions={
            selected ? (
              <Tooltip title={t('removeCalendarFromGraph')}>
                <IconButton
                  className="linked-calendar-remove nodrag nopan"
                  size="small"
                  aria-label={t('removeCalendarFromGraph')}
                  onClick={onRemove}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : undefined
          }
        />
      )}

      {!showContent && (
        <Box className="linked-calendar-placeholder" aria-hidden>
          <Box className="linked-calendar-placeholder-avatar" />
          <Box className="linked-calendar-placeholder-lines">
            <Box className="linked-calendar-placeholder-line is-primary" />
          </Box>
        </Box>
      )}
    </CalendarCardShell>
  )
}

const nodeTypes = { calendar: CalendarNode }
const edgeTypes = { removableBezier: RemovableBezierEdge }

function LinkedCalendarsEditorContent(
  props: LinkedCalendarsEditorContentProps
) {
  const theme = useTheme()
  const { showPaymentDialog } = usePayment()
  const [groups, setGroups] = useState<GroupDoc[]>([])
  const [baseGraph, setBaseGraph] = useState<CalendarGraph>({})
  const [draftGraph, setDraftGraph] = useState<CalendarGraph>({})
  const [visibleGroupIds, setVisibleGroupIds] = useState<Set<string>>(new Set())
  const [notices, setNotices] = useState<Record<string, string>>({})
  const [nodes, setNodes, onNodesChange] = useNodesState<
    Node<CalendarNodeData>
  >([])
  const [demoVisibleGroupIds, setDemoVisibleGroupIds] = useState<Set<string>>(
    () => new Set(demoCalendarDefinitions.map((entry) => entry.groupId))
  )
  const [demoRemovedEdgeIds, setDemoRemovedEdgeIds] = useState<Set<string>>(
    () => new Set()
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [statusError, setStatusError] = useState('')
  const [zoom, setZoom] = useState(1)
  const [connectingParentId, setConnectingParentId] = useState<string>()
  const [graphIssueMessagesByEdgeId, setGraphIssueMessagesByEdgeId] = useState<
    Record<string, string>
  >({})
  const [statusIssueMessagesByEdgeId, setStatusIssueMessagesByEdgeId] =
    useState<Record<string, string>>({})
  const [shakingGroupIds, setShakingGroupIds] = useState<Set<string>>(new Set())
  const [errorEdges, setErrorEdges] = useState<
    Record<string, { source: string; target: string; message: string }>
  >({})
  const [userSettings, setUserSettings] = useState<UserSettingsDoc>()
  const [accountRefreshTick, setAccountRefreshTick] = useState(0)
  const pendingTierConnectionRef = useRef<
    { edgeId: string; parentId: string; childId: string } | undefined
  >(undefined)

  const { setCenter, screenToFlowPosition } = useReactFlow()
  const isDarkMode = theme.palette.mode === 'dark'
  const userId = LocalStorage.get(LocalStorageKey.USER_ID) ?? ''
  const connectionCompletedRef = useRef(false)
  const pendingDropPositionsRef = useRef<
    Record<string, { x: number; y: number }>
  >({})
  const dragPreviewElementRef = useRef<HTMLElement | null>(null)
  const pendingFocusGroupIdRef = useRef<string | undefined>(undefined)
  const canvasPanelRef = useRef<HTMLDivElement | null>(null)
  const resizeRecenterTimeoutRef = useRef<number | undefined>(undefined)
  const initialFocusDoneRef = useRef(false)
  const shakeResetTimerRef = useRef<number | undefined>(undefined)
  const statusValidationRunRef = useRef(0)
  const initialStatusValidationDoneRef = useRef(false)

  const nodesRef = useRef<Node<CalendarNodeData>[]>([])

  const visibleIdsFromGraph = useCallback(
    (graph: CalendarGraph, sourceGroups: GroupDoc[], currentUserId: string) => {
      const allEdges = graphEdges(graph)
      const groupById = new Map(
        sourceGroups.map((group) => [group._id ?? '', group])
      )
      const parentsByChild = new Map<string, string[]>()

      allEdges.forEach((edge) => {
        const existing = parentsByChild.get(edge.childId)
        if (existing) {
          existing.push(edge.parentId)
        } else {
          parentsByChild.set(edge.childId, [edge.parentId])
        }
      })

      const stack = Array.from(
        new Set(
          allEdges
            .map((edge) => edge.childId)
            .filter((childId) =>
              isUserAdminInGroup(groupById.get(childId), currentUserId)
            )
        )
      )

      const visibleIds = new Set<string>()
      while (stack.length > 0) {
        const currentId = stack.pop() as string
        if (visibleIds.has(currentId)) continue

        visibleIds.add(currentId)
        ;(parentsByChild.get(currentId) ?? []).forEach((parentId) => {
          if (!visibleIds.has(parentId)) {
            stack.push(parentId)
          }
        })
      }

      return visibleIds
    },
    []
  )

  const centerGraphPreview = useCallback(
    (targetNodes: Node<CalendarNodeData>[], duration?: number) => {
      if (targetNodes.length === 0) return

      const center = nodeBoundsCenter(targetNodes)

      setCenter(center.x, center.y, {
        zoom: PREVIEW_ZOOM,
        duration
      })
    },
    [setCenter]
  )

  const centerInitialPreview = useCallback(
    (targetNodes: Node<CalendarNodeData>[], duration?: number) => {
      const initialNode = props.initialGroupId
        ? targetNodes.find((node) => node.id === props.initialGroupId)
        : undefined

      if (initialNode) {
        setCenter(
          initialNode.position.x + NODE_WIDTH / 2,
          initialNode.position.y + NODE_HEIGHT / 2,
          { zoom: PREVIEW_ZOOM, duration }
        )
        return
      }

      centerGraphPreview(targetNodes, duration)
    },
    [centerGraphPreview, props.initialGroupId, setCenter]
  )

  useEffect(() => {
    nodesRef.current = nodes
  }, [nodes])

  useEffect(() => {
    return () => {
      window.clearTimeout(shakeResetTimerRef.current)
    }
  }, [])

  const removeDragPreviewElement = useCallback(() => {
    const preview = dragPreviewElementRef.current
    if (!preview) return

    preview.remove()
    dragPreviewElementRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      removeDragPreviewElement()
    }
  }, [removeDragPreviewElement])

  useEffect(() => {
    const el = canvasPanelRef.current
    if (!el || typeof ResizeObserver === 'undefined') return

    let rafId = 0
    let lastWidth = el.getBoundingClientRect().width
    let lastHeight = el.getBoundingClientRect().height
    const runRecenter = () => {
      cancelAnimationFrame(rafId)
      window.clearTimeout(resizeRecenterTimeoutRef.current)
      resizeRecenterTimeoutRef.current = window.setTimeout(() => {
        rafId = requestAnimationFrame(() => {
          centerInitialPreview(nodesRef.current, 200)
        })
      }, PREVIEW_RESIZE_SETTLE_DELAY)
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      const nextWidth = entry.contentRect.width
      const nextHeight = entry.contentRect.height
      if (
        Math.abs(nextWidth - lastWidth) < 1 &&
        Math.abs(nextHeight - lastHeight) < 1
      )
        return
      lastWidth = nextWidth
      lastHeight = nextHeight
      runRecenter()
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      cancelAnimationFrame(rafId)
      window.clearTimeout(resizeRecenterTimeoutRef.current)
    }
  }, [centerInitialPreview])

  useOnViewportChange({
    onChange: (viewport) => setZoom(viewport.zoom)
  })

  useEffect(() => {
    Promise.all([
      allGroupsForInheritance(),
      AppConstantsQuery(),
      UserSettings()
    ])
      .then(([storedGroups, constants, settings]) => {
        const loadedGroups = storedGroups as unknown as GroupDoc[]
        const graph = graphFromGroups(loadedGroups)
        const linkedIds = visibleIdsFromGraph(graph, loadedGroups, userId)

        setLoadError('')
        setStatusError('')
        setGroups(loadedGroups)
        setBaseGraph(graph)
        setDraftGraph(graph)
        setVisibleGroupIds(linkedIds)
        setUserSettings(settings)

        /*
          Comment out by request of Gilad: 
          https://24meapp.atlassian.net/jira/software/c/projects/RND24ME/boards/19?quickFilter=36&selectedIssue=RND24ME-6530
          Comment from August 20, 2026: 
          inheritanceDepthLimitNotice - error on inheritance nodes when depth exceeded provided by getchanges. store the groups from the getChanges reponse like you do today, but don’t do anything with that map for now

          inheritanceTierNotice - tier error on inheritance nodes provided by getchanges. store the groups from the getChanges reponse like you do today, but don’t do anything with that map for now
        */

        // setNotices({
        //   ...Object.fromEntries(
        //     Object.keys(constants.groupsExceedsInheritanceDepthLimit ?? {}).map(
        //       (groupId) => [groupId, t('inheritanceDepthLimitNotice')]
        //     )
        //   ),
        //   ...Object.fromEntries(
        //     Object.keys(constants.groupsInheritanceBlockedByTier ?? {}).map(
        //       (groupId) => [groupId, t('inheritanceTierNotice')]
        //     )
        //   )
        // })
      })
      .catch(() => setLoadError(t('linkedCalendarsLoadFailed')))
      .finally(() => setLoading(false))
  }, [props.initialGroupId, userId, visibleIdsFromGraph])

  // Derived from the observed Account row so tier upgrades (or downgrades)
  // applied while this editor is open take effect without requiring a remount.
  const currentTier = useMemo(
    () => highestActiveTier(props.account),
    [accountRefreshTick, props.account]
  )

  const handlePurchaseDialogClose = useCallback(
    (status: 'PURCHASED' | 'PURCHASE_ABORTED' | 'DIALOG_CLOSED') => {
      if (status !== 'PURCHASED') return

      const pendingConnection = pendingTierConnectionRef.current
      pendingTierConnectionRef.current = undefined
      setAccountRefreshTick((tick) => tick + 1)

      if (
        !pendingConnection ||
        !visibleGroupIds.has(pendingConnection.parentId) ||
        !visibleGroupIds.has(pendingConnection.childId)
      ) {
        return
      }

      setStatusError('')
      setErrorEdges((previous) => {
        const next = { ...previous }
        delete next[pendingConnection.edgeId]
        return next
      })
      setDraftGraph((graph) =>
        addParentConnection(
          graph,
          pendingConnection.childId,
          pendingConnection.parentId
        )
      )
    },
    [visibleGroupIds]
  )

  const openUpgradeDialog = useCallback(() => {
    showPaymentDialog(
      tierToPlan(Tier.BUSINESS_PLUS),
      handlePurchaseDialogClose,
      [tierFeaturesToAnalyticsFeature(TierFeatures.LINK_CALENDARS)]
    )
  }, [handlePurchaseDialogClose, showPaymentDialog])

  const tierMessage = t('linkedCalendarOwnerTierRequired')
  const ownerTierMessage = t('linkedCalendarGroupOwnerTierRequired')
  const tierRedrawActive = currentTier >= Tier.BUSINESS_PLUS
  const visibleGraphIssueMessagesByEdgeId = useMemo(() => {
    const combinedIssues = {
      ...statusIssueMessagesByEdgeId,
      ...graphIssueMessagesByEdgeId
    }

    if (!tierRedrawActive) return combinedIssues

    // Hide only user-fixable tier errors after the user upgrades during
    // the open session. Keep non-owner tier issues visible.
    return Object.fromEntries(
      Object.entries(combinedIssues).filter(([, message]) => {
        return message !== tierMessage || message === ownerTierMessage
      })
    )
  }, [
    graphIssueMessagesByEdgeId,
    ownerTierMessage,
    statusIssueMessagesByEdgeId,
    tierMessage,
    tierRedrawActive
  ])

  const visibleErrorEdges = useMemo(() => {
    if (!tierRedrawActive) return errorEdges

    return Object.fromEntries(
      Object.entries(errorEdges).filter(([, entry]) => {
        return (
          entry.message !== tierMessage || entry.message === ownerTierMessage
        )
      })
    )
  }, [errorEdges, ownerTierMessage, tierMessage, tierRedrawActive])

  const dirty = useMemo(
    () => Object.keys(inheritancePayload(baseGraph, draftGraph)).length > 0,
    [baseGraph, draftGraph]
  )

  useEffect(() => {
    props.onDirtyChange?.(dirty)
  }, [dirty, props.onDirtyChange])

  const groupById = useMemo(
    () => new Map(groups.map((group) => [group._id ?? '', group])),
    [groups]
  )

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setVisibleGroupIds((ids) => {
        const nextIds = visibleIdsFromGraph(draftGraph, groups, userId)

        // Keep standalone nodes that were explicitly added from the sidebar
        // so users can stage new links before connecting edges. Validation
        // rules are enforced when creating connections.
        ids.forEach((groupId) => {
          if (groupById.has(groupId)) {
            nextIds.add(groupId)
          }
        })

        if (
          nextIds.size === ids.size &&
          Array.from(nextIds).every((groupId) => ids.has(groupId))
        ) {
          return ids
        }

        return nextIds
      })
    }, 0)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [draftGraph, groupById, groups, userId, visibleIdsFromGraph])

  const childBlockReason = useCallback(
    (group: GroupDoc | undefined) => {
      if (!isUserAdminInGroup(group, userId))
        return t('linkCalendarChildBlockedNonAdmin')
      if (group?.OwnerID === userId && currentTier < Tier.BUSINESS_PLUS) {
        return t('linkedCalendarOwnerTierRequired')
      }
      return undefined
    },
    [currentTier, userId]
  )

  const parentBlockReason = useCallback(
    (group: GroupDoc | undefined) => {
      if (!group) return t('linkCalendarParentBlockedActiveParticipant')
      if (!isUserActiveInGroup(group, userId))
        return t('linkCalendarParentBlockedActiveParticipant')
      if (!isCalendarLinkingAllowed(group))
        return t('linkCalendarParentBlockedAddingMembers')
      return undefined
    },
    [userId]
  )

  const canEditChild = useCallback(
    (groupId: string) => !childBlockReason(groupById.get(groupId)),
    [childBlockReason, groupById]
  )

  // Live block reason for the source (parent) node of an in-progress
  // connection. Used both to prevent React Flow from creating the edge and
  // to render the temporary connection line in an error state.
  const dragSourceBlockReason = useCallback(
    (parentId: string | null | undefined) => {
      if (!parentId) return undefined
      return parentBlockReason(groupById.get(parentId))
    },
    [groupById, parentBlockReason]
  )

  // Full block reason for a connection currently being dragged. Combines
  // parent-side, child-side and graph-shape errors so the temporary line
  // renders in an error state with a matching tooltip. All reasons are
  // surfaced (including tier), because the drop is always allowed and the
  // error handling in connectCalendars decides whether to open the tier
  // dialog or show an inline error.
  const dragBlockReason = useCallback(
    (source: string | null | undefined, target: string | null | undefined) => {
      if (!source) return undefined

      // When hovering a concrete child target, let child/graph checks take
      // precedence so the tooltip updates in real time to that target.
      if (target) {
        const childReason = childBlockReason(groupById.get(target))
        if (childReason) return childReason

        if (isSelfConnection(target, source)) {
          return t('linkedCalendarSelfLink')
        }
      }

      const sourceReason = dragSourceBlockReason(source)
      if (sourceReason) return sourceReason

      return undefined
    },
    [childBlockReason, dragSourceBlockReason, draftGraph, groupById]
  )

  // Always allow the drop; the visual red state and any tier/error handling
  // are driven by dragBlockReason + connectCalendars instead of preventing
  // React Flow from firing onConnect.
  const isValidConnection = useCallback<IsValidConnection>(() => true, [])

  const ConnectionLine = useMemo(() => {
    const Component = (props: ConnectionLineComponentProps) => {
      const [path, labelX, labelY] = getBezierPath({
        sourceX: props.fromX,
        sourceY: props.fromY,
        sourcePosition: props.fromPosition,
        targetX: props.toX,
        targetY: props.toY,
        targetPosition: props.toPosition
      })

      // Drive invalidity from dragBlockReason directly so the red state
      // and tooltip still show even though isValidConnection now always
      // returns true (so the drop can fire and open the tier dialog).
      const problem = dragBlockReason(props.fromNode?.id, props.toNode?.id)
      const invalid = Boolean(problem)
      const stroke = invalid ? '#ea5e46' : '#76819a'
      const markerEnd = invalid
        ? 'url(#linked-calendar-connection-arrow-invalid)'
        : 'url(#linked-calendar-connection-arrow-valid)'
      const tooltipPlacement: TooltipProps['placement'] =
        horizontalTooltipPlacement(props.fromX, props.toX)

      return (
        <>
          <defs>
            <marker
              id="linked-calendar-connection-arrow-valid"
              markerWidth="12"
              markerHeight="12"
              viewBox="-10 -10 20 20"
              markerUnits="strokeWidth"
              orient="auto"
              refX="0"
              refY="0"
            >
              <polyline
                points="-5,-4 0,0 -5,4 -5,-4"
                fill={stroke}
                stroke={stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
            <marker
              id="linked-calendar-connection-arrow-invalid"
              markerWidth="12"
              markerHeight="12"
              viewBox="-10 -10 20 20"
              markerUnits="strokeWidth"
              orient="auto"
              refX="0"
              refY="0"
            >
              <polyline
                points="-5,-4 0,0 -5,4 -5,-4"
                fill={stroke}
                stroke={stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
          </defs>
          <BaseEdge
            path={path}
            markerEnd={markerEnd}
            style={{
              fill: 'none',
              stroke,
              strokeWidth: 2,
              strokeDasharray: invalid ? undefined : '4 6'
            }}
          />
          {invalid && problem && (
            <EdgeLabelRenderer>
              <Tooltip
                open
                arrow
                placement={tooltipPlacement}
                title={connectionTooltipTitle(
                  problem,
                  problem === tierMessage ? openUpgradeDialog : undefined
                )}
                slotProps={{
                  popper: {
                    disablePortal: true
                  },
                  tooltip: {
                    className: 'linked-calendar-error-tooltip',
                    sx: {
                      direction: 'ltr',
                      fontSize: 14,
                      maxWidth: 260,
                      padding: '8px'
                    }
                  }
                }}
              >
                <Box
                  className="linked-calendar-connection-problem"
                  sx={{
                    transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`
                  }}
                >
                  <SyncProblem fontSize="small" />
                </Box>
              </Tooltip>
            </EdgeLabelRenderer>
          )}
        </>
      )
    }
    return Component
  }, [dragBlockReason, openUpgradeDialog, tierMessage])

  const clearAllGraphIssues = useCallback(() => {
    setGraphIssueMessagesByEdgeId({})
    setStatusIssueMessagesByEdgeId({})
  }, [])

  const clearGraphIssues = useCallback((edgeId?: string) => {
    if (!edgeId) {
      setGraphIssueMessagesByEdgeId({})
      return
    }

    setGraphIssueMessagesByEdgeId((existingIssues) => {
      if (!existingIssues[edgeId]) return existingIssues

      const nextIssues = { ...existingIssues }
      delete nextIssues[edgeId]
      return nextIssues
    })

    setStatusIssueMessagesByEdgeId((existingIssues) => {
      if (!existingIssues[edgeId]) return existingIssues

      const nextIssues = { ...existingIssues }
      delete nextIssues[edgeId]
      return nextIssues
    })
  }, [])

  const showGraphIssues = useCallback((issues: GraphIssueEdge[]) => {
    setGraphIssueMessagesByEdgeId((existingIssues) =>
      mergeGraphIssues(existingIssues, issues)
    )

    if (issues.length === 0) return

    setErrorEdges((prev) => {
      const next = { ...prev }
      issues.forEach((issue) => {
        if (
          !issue.parentId ||
          !issue.childId ||
          issue.parentId === issue.childId
        )
          return
        const edgeId = `${issue.parentId}->${issue.childId}`
        next[edgeId] = {
          source: issue.parentId,
          target: issue.childId,
          message: issue.message
        }
      })
      return next
    })

    setVisibleGroupIds((ids) => {
      const next = new Set(ids)
      issues.forEach((issue) => {
        if (issue.parentId) next.add(issue.parentId)
        if (issue.childId) next.add(issue.childId)
      })
      return next
    })

    const shakingIds = new Set<string>()
    issues.forEach((issue) => {
      shakingIds.add(issue.parentId)
      shakingIds.add(issue.childId)
    })

    setShakingGroupIds(shakingIds)
    window.clearTimeout(shakeResetTimerRef.current)
    shakeResetTimerRef.current = window.setTimeout(() => {
      setShakingGroupIds(new Set())
    }, 620)
  }, [])

  const statusSubjectsForGraph = useCallback(
    (graph: CalendarGraph, sourceGroups: GroupDoc[]) => {
      const groupMap = new Map(sourceGroups.map((group) => [group._id, group]))
      const childIds = Array.from(
        new Set(graphEdges(graph).map((edge) => edge.childId))
      )

      return childIds.filter((childId) =>
        isUserAdminInGroup(groupMap.get(childId), userId)
      )
    },
    [userId]
  )

  const validateGraphStatus = useCallback(
    async (graph: CalendarGraph, sourceGroups: GroupDoc[]) => {
      const runId = ++statusValidationRunRef.current
      const subjectGroupIds = statusSubjectsForGraph(graph, sourceGroups)

      if (subjectGroupIds.length === 0) {
        if (runId === statusValidationRunRef.current) {
          setStatusIssueMessagesByEdgeId({})
        }
        return
      }

      try {
        const batches = chunkArray(
          subjectGroupIds,
          MAX_STATUS_SUBJECTS_PER_REQUEST
        )
        const aggregatedIssues: InheritanceIssue[] = []

        for (const batch of batches) {
          const statusResult = await inheritanceStatus(batch)
          if ((statusResult.issues?.length ?? 0) > 0) {
            aggregatedIssues.push(...statusResult.issues)
          }
        }

        if (runId !== statusValidationRunRef.current) return

        setStatusError('')
        const mappedIssues = aggregatedIssues
          .filter((issue) => issue.childGroupId && issue.parentGroupId)
          .map((issue) => ({
            parentId: issue.parentGroupId,
            childId: issue.childGroupId,
            message: inheritanceIssueMessage(issue)
          }))

        setStatusIssueMessagesByEdgeId(
          Object.fromEntries(
            mappedIssues.map((issue) => [
              `${issue.parentId}->${issue.childId}`,
              issue.message
            ])
          )
        )
      } catch {
        if (runId !== statusValidationRunRef.current) return
        setStatusError(t('linkedCalendarsLoadFailed'))
      }
    },
    [statusSubjectsForGraph]
  )
  // Record a persistent red edge for a failed drop so the user still sees
  // the attempted connection on the canvas (they can remove it via the X
  // button on the edge). Both endpoints are added to the visible set so a
  // dropped-onto sidebar calendar becomes visible.
  const recordErrorEdge = useCallback(
    (parentId: string, childId: string, message: string) => {
      if (!parentId || !childId || parentId === childId) return
      const edgeId = `${parentId}->${childId}`
      setErrorEdges((prev) => ({
        ...prev,
        [edgeId]: { source: parentId, target: childId, message }
      }))
      setVisibleGroupIds((ids) => {
        const next = new Set(ids)
        next.add(parentId)
        next.add(childId)
        return next
      })
      setShakingGroupIds(new Set([parentId, childId]))
      window.clearTimeout(shakeResetTimerRef.current)
      shakeResetTimerRef.current = window.setTimeout(() => {
        setShakingGroupIds(new Set())
      }, 620)
    },
    []
  )

  const handleRemoveEdge = useCallback(
    (edgeId: string) => {
      const [parentId, childId] = edgeId.split('->')
      if (!parentId || !childId) return

      // Phantom red edge (from a failed drop) — just drop the record; there
      // is no corresponding draftGraph entry to touch.
      if (visibleErrorEdges[edgeId]) {
        if (pendingTierConnectionRef.current?.edgeId === edgeId) {
          pendingTierConnectionRef.current = undefined
        }
        setErrorEdges((prev) => {
          const next = { ...prev }
          delete next[edgeId]
          return next
        })
        clearGraphIssues(edgeId)
        return
      }

      setStatusError('')
      clearGraphIssues(edgeId)
      setDraftGraph((graph) => removeParentConnection(graph, childId, parentId))
    },
    [clearGraphIssues, visibleErrorEdges]
  )

  const handleModeChange = useCallback(
    (edgeId: string, mode: ParentGroupLinkValue) => {
      const [parentId, childId] = edgeId.split('->')
      if (!parentId || !childId || !canEditChild(childId)) return

      setStatusError('')
      clearGraphIssues(edgeId)
      setDraftGraph((graph) =>
        setParentConnectionMode(graph, childId, parentId, mode)
      )
    },
    [canEditChild, clearGraphIssues]
  )

  const nodeCenters = useMemo(() => {
    const map = new Map<string, { x: number; y: number }>()
    nodes.forEach((node) => {
      map.set(node.id, {
        x: node.position.x + NODE_WIDTH / 2,
        y: node.position.y + NODE_HEIGHT / 2
      })
    })
    return map
  }, [nodes])

  const visibleEdges = useMemo(() => {
    const tierMessage = t('linkedCalendarOwnerTierRequired')
    const edgeIds = new Set(graphEdges(draftGraph).map((edge) => edge.id))

    const buildEdge = (
      id: string,
      source: string,
      target: string,
      isRed: boolean,
      errorMessage: string | undefined,
      canRemove: boolean,
      mode?: ParentGroupLinkValue
    ): Edge<GraphEdgeData> => {
      const sourceCenter = nodeCenters.get(source)
      const targetCenter = nodeCenters.get(target)
      const handles =
        sourceCenter && targetCenter
          ? graphEdgeHandles(
              source,
              target,
              sourceCenter,
              targetCenter,
              edgeIds.has(`${target}->${source}`)
            )
          : { source: 'bottom', target: 'top' }
      const tooltipPlacement: TooltipProps['placement'] =
        sourceCenter && targetCenter
          ? horizontalTooltipPlacement(sourceCenter.x, targetCenter.x)
          : 'right'

      return {
        id,
        source,
        target,
        type: 'removableBezier',
        sourceHandle: handles.source,
        targetHandle: handles.target,
        animated: mode !== ParentGroupLinkMode.Terminal,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isRed ? '#ea5e46' : '#76819a'
        },
        style: {
          stroke: isRed ? '#ea5e46' : '#76819a',
          strokeDasharray:
            isRed || mode === ParentGroupLinkMode.Terminal ? undefined : '4 6',
          strokeWidth: 2
        },
        data: {
          canRemove,
          reverseEdgeExists: edgeIds.has(`${target}->${source}`),
          mode,
          errorMessage,
          tooltipPlacement,
          onUpgrade:
            errorMessage === tierMessage ? openUpgradeDialog : undefined,
          onRemove: handleRemoveEdge,
          onModeChange: handleModeChange
        }
      } as Edge<GraphEdgeData>
    }

    const graphEdgeItems = graphEdges(draftGraph)
      .filter(
        (edge) =>
          visibleGroupIds.has(edge.source) && visibleGroupIds.has(edge.target)
      )
      .map((edge) => {
        const errorMessage =
          visibleGraphIssueMessagesByEdgeId[edge.id] ??
          visibleErrorEdges[edge.id]?.message
        const isRed = Boolean(errorMessage)

        return buildEdge(
          edge.id,
          edge.source,
          edge.target,
          isRed,
          errorMessage,
          canEditChild(edge.childId),
          draftGraph[edge.childId]?.[edge.parentId]
        )
      })

    const existingIds = new Set(graphEdgeItems.map((e) => e.id))
    const phantomItems = Object.entries(visibleErrorEdges)
      .filter(([id, info]) => {
        if (existingIds.has(id)) return false
        return (
          visibleGroupIds.has(info.source) && visibleGroupIds.has(info.target)
        )
      })
      .map(([id, info]) =>
        buildEdge(id, info.source, info.target, true, info.message, true)
      )

    return [...graphEdgeItems, ...phantomItems]
  }, [
    canEditChild,
    visibleGraphIssueMessagesByEdgeId,
    draftGraph,
    visibleErrorEdges,
    handleRemoveEdge,
    handleModeChange,
    nodeCenters,
    openUpgradeDialog,
    visibleGroupIds
  ])

  const layoutedNodes = useMemo(() => {
    const showDetails = zoom >= CONTENT_ZOOM_THRESHOLD
    const visibleNodes = Array.from(visibleGroupIds).map((groupId) => {
      const group = groupById.get(groupId)

      return {
        id: groupId,
        type: 'calendar',
        selected: groupId === props.initialGroupId,
        position: { x: 0, y: 0 },
        data: {
          groupId,
          name: group?.Name ?? t('nonMemberCalendar'),
          color: group?.GroupColor ?? '#7f8aa5',
          photo: group?.Photo,
          fallbackPhoto: group
            ? group._id === 'tempid'
              ? calendarsGif
              : defaultGroupImage(group)
            : undefined,
          showDetails,
          isShaking: shakingGroupIds.has(groupId),
          warning: notices[groupId],
          onRemove: () => {}
        }
      } as Node<CalendarNodeData>
    })

    return layoutNodes(visibleNodes, visibleEdges)
  }, [
    groupById,
    notices,
    props.initialGroupId,
    shakingGroupIds,
    visibleEdges,
    visibleGroupIds,
    zoom
  ])

  const removeNode = useCallback(
    (groupId: string) => {
      const nextGraph = removeCalendarNode(draftGraph, groupId)

      setStatusError('')
      clearAllGraphIssues()
      setDraftGraph(nextGraph)
      setVisibleGroupIds((ids) => {
        const nextIds = new Set(ids)
        nextIds.delete(groupId)
        return nextIds
      })
      setNodes((currentNodes) =>
        currentNodes.filter((node) => node.id !== groupId)
      )
    },
    [clearGraphIssues, draftGraph, setNodes]
  )

  useEffect(() => {
    setNodes((currentNodes) => {
      const currentById = new Map(currentNodes.map((node) => [node.id, node]))
      let changed = currentNodes.length !== layoutedNodes.length

      const nextNodes = layoutedNodes.map((node, index) => {
        const existing = currentById.get(node.id)
        const nextData = { ...node.data, onRemove: removeNode }

        if (existing) {
          const existingData = existing.data as CalendarNodeData
          const dataChanged =
            existingData.groupId !== nextData.groupId ||
            existingData.name !== nextData.name ||
            existingData.color !== nextData.color ||
            existingData.photo !== nextData.photo ||
            existingData.fallbackPhoto !== nextData.fallbackPhoto ||
            existingData.showDetails !== nextData.showDetails ||
            existingData.isShaking !== nextData.isShaking ||
            existingData.warning !== nextData.warning ||
            existingData.onRemove !== nextData.onRemove

          const nodeOrderChanged = currentNodes[index]?.id !== node.id
          changed = changed || nodeOrderChanged || dataChanged

          if (!dataChanged) {
            return existing
          }

          // Preserve React Flow internal fields (measured, dragging, selected, position)
          // by keeping the previous node object and only updating data.
          return { ...existing, data: nextData }
        }

        const droppedPosition = pendingDropPositionsRef.current[node.id]
        changed = changed || !existing
        if (droppedPosition) {
          delete pendingDropPositionsRef.current[node.id]
          return { ...node, data: nextData, position: droppedPosition }
        }

        return { ...node, data: nextData }
      })

      return changed ? nextNodes : currentNodes
    })
  }, [layoutedNodes, removeNode, setNodes])

  useEffect(() => {
    if (loading || nodes.length === 0 || initialFocusDoneRef.current) return
    initialFocusDoneRef.current = true
    const initialCenterDelay = props.initialGroupId
      ? PREVIEW_RESIZE_SETTLE_DELAY
      : 0
    const timerId = window.setTimeout(() => {
      requestAnimationFrame(() => {
        centerInitialPreview(nodes, 200)
      })
    }, initialCenterDelay)

    return () => window.clearTimeout(timerId)
  }, [centerInitialPreview, loading, nodes])

  useEffect(() => {
    const groupId = pendingFocusGroupIdRef.current
    if (!groupId || nodes.length === 0) return

    const targetNode = nodes.find((node) => node.id === groupId)
    if (!targetNode) return

    pendingFocusGroupIdRef.current = undefined
    setCenter(
      targetNode.position.x + NODE_WIDTH / 2,
      targetNode.position.y + NODE_HEIGHT / 2,
      {
        zoom: PREVIEW_ZOOM,
        duration: 220
      }
    )
  }, [nodes, setCenter])

  useEffect(() => {
    if (loading || initialStatusValidationDoneRef.current) return
    initialStatusValidationDoneRef.current = true

    const timerId = window.setTimeout(() => {
      void validateGraphStatus(draftGraph, groups)
    }, 0)

    return () => {
      window.clearTimeout(timerId)
    }
  }, [draftGraph, groups, loading, validateGraphStatus])

  const connectCalendars = useCallback(
    (parentId?: string | null, childId?: string | null) => {
      if (!childId || !parentId) return

      clearGraphIssues(`${parentId}->${childId}`)

      const childGroup = groupById.get(childId)
      const childReason = childBlockReason(childGroup)
      const parentReason = parentBlockReason(groupById.get(parentId))

      if (childReason) {
        if (childReason === tierMessage) {
          pendingTierConnectionRef.current = {
            edgeId: `${parentId}->${childId}`,
            parentId,
            childId
          }
        }
        recordErrorEdge(parentId, childId, childReason)
        return
      }

      if (parentReason) {
        recordErrorEdge(parentId, childId, parentReason)
        return
      }

      if (isSelfConnection(childId, parentId)) {
        // Self-link: no edge to place on the graph, just flash the node.
        showGraphIssues([
          {
            parentId,
            childId,
            message: t('linkedCalendarSelfLink')
          }
        ])
        return
      }

      setStatusError('')
      // If the same pair was previously recorded as a phantom error edge,
      // drop that record now that a real edge is being created.
      const phantomId = `${parentId}->${childId}`
      if (errorEdges[phantomId]) {
        setErrorEdges((prev) => {
          const next = { ...prev }
          delete next[phantomId]
          return next
        })
      }
      setDraftGraph((graph) => addParentConnection(graph, childId, parentId))
    },
    [
      childBlockReason,
      clearGraphIssues,
      draftGraph,
      errorEdges,
      groupById,
      parentBlockReason,
      recordErrorEdge,
      showGraphIssues
    ]
  )

  const onConnectStart = useCallback(
    (_event: MouseEvent | TouchEvent, params: OnConnectStartParams) => {
      connectionCompletedRef.current = false
      const parentId = params.nodeId ?? undefined

      if (!parentId) {
        setConnectingParentId(undefined)
        return
      }

      setStatusError('')
      clearGraphIssues()
      setConnectingParentId(parentId)
    },
    [clearGraphIssues]
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      connectionCompletedRef.current = true
      // Drag origin is the PARENT; drop target is the CHILD.
      connectCalendars(connection.source, connection.target)
    },
    [connectCalendars]
  )

  const onConnectEnd = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!connectingParentId || connectionCompletedRef.current) {
        setConnectingParentId(undefined)
        return
      }

      const childId = nodeIdAtEventPoint(event)
      connectCalendars(connectingParentId, childId)
      setConnectingParentId(undefined)
    },
    [connectCalendars, connectingParentId]
  )

  // Delegate React Flow's built-in node deletion (Delete/Backspace) to
  // removeNode so grandparent→grandchild bypass runs uniformly for both
  // the inline remove button and the keyboard shortcut.
  const onNodesDelete = useCallback(
    (deleted: Node<CalendarNodeData>[]) => {
      deleted.forEach((node) => removeNode(node.id))
    },
    [removeNode]
  )

  const sidebarGroups = useMemo(
    () =>
      groups
        .filter(
          (group) =>
            !visibleGroupIds.has(group._id ?? '') &&
            group.Status !== '0' &&
            isUserActiveInGroup(group, userId) &&
            !isGroupArchived(userSettings, group._id ?? '') &&
            !isGroupDeleted(userSettings, group._id ?? '')
        )
        .map((group) => ({ group })),
    [groups, userId, userSettings, visibleGroupIds]
  )

  const showDemoCalendars =
    nodes.length === 0 && !dirty && demoVisibleGroupIds.size > 0

  const saveDisabled = !dirty || saving

  const startDragCalendar = (
    event: DragEvent<HTMLDivElement>,
    groupId: string
  ) => {
    removeDragPreviewElement()

    const card = event.currentTarget.querySelector(
      '.linked-calendar-card'
    ) as HTMLElement | null

    if (card) {
      const preview = card.cloneNode(true) as HTMLElement
      const cardRect = card.getBoundingClientRect()
      const previewAddButton = preview.querySelector(
        '.linked-calendars-sidebar-add-btn'
      )
      previewAddButton?.remove()

      preview.style.position = 'fixed'
      preview.style.top = '-10000px'
      preview.style.left = '-10000px'
      preview.style.margin = '0'
      preview.style.pointerEvents = 'none'
      preview.style.boxSizing = 'border-box'
      preview.style.transform = 'none'
      preview.style.borderRadius = '16px'
      preview.style.overflow = 'hidden'
      preview.style.width = `${cardRect.width}px`

      document.body.appendChild(preview)
      dragPreviewElementRef.current = preview

      const offsetX = Math.min(
        Math.max(event.clientX - cardRect.left, 0),
        cardRect.width
      )
      const offsetY = Math.min(
        Math.max(event.clientY - cardRect.top, 0),
        cardRect.height
      )

      event.dataTransfer.setDragImage(preview, offsetX, offsetY)
    }

    // Set both generic and app-specific MIME keys for better
    // cross-browser + library interop during native HTML5 DnD.
    event.dataTransfer.setData('text/plain', groupId)
    event.dataTransfer.setData('application/reactflow', groupId)
    event.dataTransfer.effectAllowed = 'move'
  }

  const endDragCalendar = (_event: DragEvent<HTMLDivElement>) => {
    removeDragPreviewElement()
  }

  const onCanvasDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const onCanvasDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()

    const groupId =
      event.dataTransfer.getData('application/reactflow') ||
      event.dataTransfer.getData('text/plain')

    const group = groupById.get(groupId)

    if (!groupId || visibleGroupIds.has(groupId) || !group) {
      return
    }

    const flowPosition = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    })

    pendingDropPositionsRef.current[groupId] = {
      x: flowPosition.x - NODE_WIDTH / 2,
      y: flowPosition.y - NODE_HEIGHT / 2
    }

    if (showDemoCalendars) {
      pendingFocusGroupIdRef.current = groupId
    }
    setVisibleGroupIds((ids) => new Set(ids).add(groupId))
  }

  const addGroupFromSidebar = useCallback(
    (groupId: string) => {
      const group = groupById.get(groupId)
      if (!groupId || visibleGroupIds.has(groupId) || !group) return

      const margin = 32
      const rightmostX = nodes.reduce(
        (maxX, node) => Math.max(maxX, node.position.x + NODE_WIDTH),
        80
      )

      pendingDropPositionsRef.current[groupId] = {
        x: rightmostX + margin,
        y: nodes.length > 0 ? nodes[0].position.y : 80
      }

      if (showDemoCalendars) {
        pendingFocusGroupIdRef.current = groupId
      }
      setVisibleGroupIds((ids) => new Set(ids).add(groupId))
    },
    [groupById, nodes, showDemoCalendars, visibleGroupIds]
  )

  const save = async () => {
    const changedPayload = inheritancePayload(baseGraph, draftGraph)
    if (Object.keys(changedPayload).length === 0 || saving) return
    const payload = changedPayload

    setSaving(true)
    setStatusError('')
    clearAllGraphIssues()

    const resolveParentIdsForSaveError = (item: InheritFromGroupsError) => {
      const parentIds = new Set<string>([
        ...(item.parentGroups ?? []),
        ...Object.keys(payload[item.childGroupId] ?? {}),
        ...Object.keys(draftGraph[item.childGroupId] ?? {}),
        ...Object.keys(baseGraph[item.childGroupId] ?? {})
      ])

      parentIds.delete(item.childGroupId)

      return Array.from(parentIds).filter((id) => id.length > 0)
    }

    const applySaveErrors = (items: InheritFromGroupsError[]) => {
      if (items.length === 0) return false

      const issues: GraphIssueEdge[] = []

      items.forEach((item) => {
        const parentIds = resolveParentIdsForSaveError(item)

        parentIds.forEach((parentId) => {
          issues.push({
            parentId,
            childId: item.childGroupId,
            message: inheritanceIssueMessage({
              code: item.code,
              reason: item.error
            })
          })
        })
      })

      if (issues.length > 0) {
        showGraphIssues(issues)
        return true
      }

      return false
    }

    const applyApiIssues = (items: InheritanceIssue[]) => {
      if (items.length === 0) return false

      const issues = items
        .filter((item) => item.parentGroupId && item.childGroupId)
        .map((item) => ({
          parentId: item.parentGroupId,
          childId: item.childGroupId,
          message: inheritanceIssueMessage(item)
        }))

      if (issues.length > 0) {
        showGraphIssues(issues)
        return true
      }

      return false
    }

    try {
      const result = await inheritFromGroups(payload)
      if (
        result.success &&
        (result.errors?.length ?? 0) === 0 &&
        (result.issues?.length ?? 0) === 0
      ) {
        clearAllGraphIssues()
        setErrorEdges({})
        setStatusError('')
        setBaseGraph(draftGraph)
      } else {
        const hasIssueEdges = applyApiIssues(result.issues ?? [])
        const hasErrorEdges = applySaveErrors(result.errors ?? [])

        if (!hasIssueEdges && !hasErrorEdges) {
          setStatusError(result.message || t('linkedCalendarsSaveFailed'))
        }
      }
    } catch (saveError: any) {
      const responseIssues =
        (saveError?.response?.data?.issues as InheritanceIssue[]) ?? []
      const responseErrors =
        (saveError?.response?.data?.errors as InheritFromGroupsError[]) ?? []

      const hasIssueEdges = applyApiIssues(responseIssues)
      const hasErrorEdges = applySaveErrors(responseErrors)

      if (!hasIssueEdges && !hasErrorEdges) {
        setStatusError(t('linkedCalendarsSaveFailed'))
      }
    } finally {
      setSaving(false)
    }
  }

  const resetCanvas = useCallback(() => {
    const nextVisibleGroupIds = visibleIdsFromGraph(baseGraph, groups, userId)

    setDraftGraph(baseGraph)
    setVisibleGroupIds(nextVisibleGroupIds)
    setStatusError('')
    clearAllGraphIssues()
    setErrorEdges({})
    setShakingGroupIds(new Set())
    setConnectingParentId(undefined)
    setZoom(PREVIEW_ZOOM)
    setNodes([])

    pendingDropPositionsRef.current = {}
    connectionCompletedRef.current = false
    initialFocusDoneRef.current = false
    window.clearTimeout(shakeResetTimerRef.current)
  }, [
    baseGraph,
    clearAllGraphIssues,
    groups,
    setNodes,
    userId,
    visibleIdsFromGraph
  ])

  if (loading) {
    return (
      <Box className="linked-calendars-loading">
        <CircularProgress size={30} />
      </Box>
    )
  }

  return (
    <Box
      className={`linked-calendars-builder${props.withCloseButton ? '' : ' linked-calendars-builder-embedded'}`}
    >
      <Box className="linked-calendars-header">
        <Box
          alignItems={'center'}
          justifyContent={'space-between'}
          display={'flex'}
          flexDirection={'row'}
        >
          <Typography className="linked-calendars-title" variant="h5">
            {t('linkedCalendarsTitle')}
          </Typography>
          {props.withCloseButton && (
            <IconButton aria-label={t('close')} onClick={props.closeAction}>
              <Close />
            </IconButton>
          )}
        </Box>

        <Typography className="linked-calendars-description">
          {t('linkedCalendarsPlanDescription')}
        </Typography>
        {(loadError || statusError) && (
          <Typography className="linked-calendars-canvas-error">
            {loadError || statusError}
          </Typography>
        )}
      </Box>

      <Box className="linked-calendars-workspace">
        <Box
          className="linked-calendars-canvas-panel"
          ref={canvasPanelRef}
          onDragOverCapture={onCanvasDragOver}
          onDropCapture={onCanvasDrop}
        >
          <Typography className="linked-calendars-canvas-label">
            {t(
              showDemoCalendars
                ? 'linkedCalendarsDemoTitle'
                : 'linkedCalendarsCanvasTitle'
            )}
          </Typography>
          {nodes.length === 0 ? (
            showDemoCalendars ? (
              <LinkedCalendarsEmptyCanvas
                visibleGroupIds={demoVisibleGroupIds}
                onVisibleGroupIdsChange={setDemoVisibleGroupIds}
                removedEdgeIds={demoRemovedEdgeIds}
                onRemovedEdgeIdsChange={setDemoRemovedEdgeIds}
              />
            ) : (
              <Box className="linked-calendars-empty">
                <Typography>{t('linkedCalendarsEmpty')}</Typography>
              </Box>
            )
          ) : (
            <ReactFlow
              nodes={nodes}
              edges={visibleEdges}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              onNodesChange={onNodesChange}
              onNodesDelete={onNodesDelete}
              onConnectStart={onConnectStart}
              onConnect={onConnect}
              onConnectEnd={onConnectEnd}
              onInit={(reactFlow) => {
                const groupId = pendingFocusGroupIdRef.current
                const targetNode = groupId
                  ? nodes.find((node) => node.id === groupId)
                  : undefined

                if (!groupId || !targetNode) return

                pendingFocusGroupIdRef.current = undefined
                reactFlow.setCenter(
                  targetNode.position.x + NODE_WIDTH / 2,
                  targetNode.position.y + NODE_HEIGHT / 2,
                  {
                    zoom: PREVIEW_ZOOM,
                    duration: 220
                  }
                )
              }}
              isValidConnection={isValidConnection}
              connectionLineComponent={ConnectionLine}
              nodesDraggable
              connectionMode={ConnectionMode.Loose}
              panOnScroll
              minZoom={0.45}
              maxZoom={1.6}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={20} size={1} color="#d7deeb" />
              <Controls showInteractive={false} />
              <MiniMap
                className="linked-calendars-minimap"
                position="bottom-right"
                pannable
                zoomable
                nodeStrokeWidth={2}
                maskColor={
                  isDarkMode
                    ? 'rgba(13, 18, 28, 0.34)'
                    : 'rgba(44, 57, 85, 0.14)'
                }
                maskStrokeColor={
                  isDarkMode
                    ? 'rgba(255, 255, 255, 0.72)'
                    : 'rgba(44, 57, 85, 0.46)'
                }
                maskStrokeWidth={2}
                nodeColor={(node) =>
                  ((node.data as CalendarNodeData | undefined)?.color as
                    | string
                    | undefined) ?? '#95a2bf'
                }
              />
            </ReactFlow>
          )}
        </Box>

        <Box className="linked-calendars-sidebar-panel">
          <Box className="linked-calendars-sidebar-title">
            {t('availableCalendars')}
          </Box>
          <List
            className="linked-calendars-sidebar-list"
            sx={{
              paddingInlineEnd: '12px',
              paddingTop: 0,
              paddingBottom: '8px'
            }}
          >
            <Reorder.Group
              as="div"
              axis="y"
              values={sidebarGroups.map(({ group }) => group._id ?? '')}
              onReorder={() => {}}
            >
              <AnimatePresence initial={false}>
                {sidebarGroups.map(({ group }) => {
                  const groupId = group._id ?? ''
                  return (
                    <Reorder.Item
                      style={{ listStyle: 'none' }}
                      as="div"
                      key={groupId}
                      value={groupId}
                      dragListener={false}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      transition={{
                        duration: 0.2,
                        ease: [0.22, 1, 0.36, 1]
                      }}
                    >
                      <Box
                        className="linked-calendars-sidebar-item"
                        draggable
                        role="button"
                        tabIndex={0}
                        aria-label={t('add')}
                        onClick={() => addGroupFromSidebar(groupId)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            addGroupFromSidebar(groupId)
                          }
                        }}
                        onDragStart={(event) => {
                          startDragCalendar(event, groupId)
                        }}
                        onDragEnd={endDragCalendar}
                      >
                        <CalendarCardShell
                          color={group.GroupColor ?? '#6377af'}
                        >
                          <CalendarChipVisual
                            name={group.Name ?? t('nonMemberCalendar')}
                            color={group.GroupColor ?? '#6377af'}
                            photo={group.Photo}
                            fallbackPhoto={defaultGroupImage(group)}
                          />
                          <IconButton
                            size="small"
                            className="linked-calendars-sidebar-add-btn"
                            aria-label={t('add')}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                        </CalendarCardShell>
                      </Box>
                    </Reorder.Item>
                  )
                })}
              </AnimatePresence>
            </Reorder.Group>
          </List>

          <Stack
            className="linked-calendars-actions"
            direction="row"
            justifyContent="flex-end"
          >
            {props.closeAction && (
              <Button
                className="linked-calendars-cancel-button"
                variant="text"
                disabled={saving || !dirty}
                onClick={resetCanvas}
                sx={{ color: (theme) => theme.palette.text.primary }}
              >
                {t('undoChanges')}
              </Button>
            )}
            <Button disabled={saveDisabled} variant="contained" onClick={save}>
              {saving ? t('saving') : t('save')}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  )
}

const LinkedCalendarsEditorContentWithAccount = withObservables([], () => ({
  account: ObserveService.Account()
}))(
  LinkedCalendarsEditorContent as ComponentType<unknown>
) as ComponentType<LinkedCalendarsEditorProps>

export default function LinkedCalendarsEditor(
  props: LinkedCalendarsEditorProps
) {
  return (
    <ReactFlowProvider>
      <LinkedCalendarsEditorContentWithAccount {...props} />
    </ReactFlowProvider>
  )
}
